import { useState, useMemo, useCallback, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card.jsx";
import { Button } from "@/components/ui/button.jsx";
import { Input } from "@/components/ui/input.jsx";
import { Checkbox } from "@/components/ui/checkbox.jsx";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table.jsx";
import { Search, X, CheckSquare } from "lucide-react";
import { cn } from "@/lib/utils.js";
import { removeCommasAndSpaces } from "@/lib/formatting-utils.js";
import { cardStyles, infoTooltipIcon, searchStyles, steelChromeButton, steelRowSelected } from "@/lib/status-styles.js";
import {
  matchesStatusFilter,
  countParametersByStatus,
  getParameterStatus,
} from "@/lib/parameter-status-utils.js";
import ParameterStatusBadge from "./parameter-status-badge.jsx";
import { formatNumber } from "@/lib/formatting-utils.js";
import { getTagMeasurementsLast7Days } from "@/api/measurement-api.js";
import {
  calculateAverageAndZScore,
  filterParametersByGroups,
} from "@/lib/tag-data-utils.js";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip.jsx";
import { Info } from "lucide-react";
import MiniTrendSparkline from "./mini-trend-sparkline.jsx";
import MiniHorizontalGauge from "./mini-horizontal-gauge.jsx";
import LoadingIndicator from "./ui/loading-indicator.jsx";
import SortableTableHeader from "./ui/sortable-table-header.jsx";
import GroupsDropdown from "./groups-dropdown.jsx";
import {
  ENALYSIS_BLOWBY_CATEGORY,
  ENALYSIS_FILTER_ALL_SENTINEL,
  ENALYSIS_METRICS_CATEGORY,
  ENALYSIS_ROD_LOADS_CATEGORY,
  ENALYSIS_TREND_ROW_CATEGORIES,
  ENALYSIS_UTILIZATION_CATEGORY,
} from "@/constants/enalysis-trends-constants.js";
import { MAX_SELECTED_GRAPH_TAGS } from "@/constants/chart-constants.js";

const GRAPH_TAG_LIMIT_TITLE = `Maximum of ${MAX_SELECTED_GRAPH_TAGS} tags can be graphed`;

/**
 * @param {Set<string>} selected
 * @param {string[]} candidateIds
 */
function addGraphTagSelections(selected, candidateIds) {
  const next = new Set(selected);
  for (const id of candidateIds) {
    if (next.size >= MAX_SELECTED_GRAPH_TAGS) break;
    next.add(id);
  }
  return next;
}

/**
 * @param {Set<string>} selected
 * @param {string[]} visibleIds
 */
function removeVisibleGraphTagSelections(selected, visibleIds) {
  const visibleIdSet = new Set(visibleIds);
  return new Set(
    Array.from(selected).filter((id) => !visibleIdSet.has(id))
  );
}

function isEnalysisTrendParamRow(param) {
  return ENALYSIS_TREND_ROW_CATEGORIES.has(param?.category);
}

/**
 * Tag List Panel Component
 * Displays search, filters, and parameters table
 */
export default function TagListPanel({
  allParameters,
  groups = [],
  isLoadingGroups = false,
  selectedParameters,
  setSelectedParameters,
  searchTerm,
  setSearchTerm,
  selectedStatus,
  setSelectedStatus,
  selectedCategory,
  setSelectedCategory,
  selectedGroupId = null,
  setSelectedGroupId = () => {},
  sortColumn,
  sortDirection,
  handleSort,
  getParameterId,
  formatValue,
  trendColumns, // Optional: pre-calculated trend columns from parent
  trendColumnRawData, // Optional: raw 7-day data for sparklines
  isLoadingTrendColumns = false, // Loading state for trend columns
  isLoadingEnalysisTrendValues = false,
  /** When true, stack filter row (narrow resizable column beside trend chart) */
  compactFilterToolbar = false,
}) {
  // State to store trend columns for each parameter (fallback if not provided)
  const [localTrendColumns, setLocalTrendColumns] = useState(new Map());

  // Use provided trend columns or local trend columns
  const stats = trendColumns || localTrendColumns;

  // Get unique categories
  const categories = useMemo(() => {
    if (!allParameters) return [];
    const categorySet = new Set();
    allParameters.forEach((param) => {
      if (param.category) {
        categorySet.add(param.category);
      }
    });
    return Array.from(categorySet).filter(
      (cat) =>
        !ENALYSIS_TREND_ROW_CATEGORIES.has(cat)
        && allParameters.some((p) => p.category === cat)
    );
  }, [allParameters]);

  const enalysisCounts = useMemo(() => {
    const z = { total: 0, utilization: 0, rodLoads: 0, blowby: 0 };
    if (!allParameters?.length) return z;
    for (const p of allParameters) {
      if (!ENALYSIS_TREND_ROW_CATEGORIES.has(p.category)) continue;
      z.total++;
      if (p.category === ENALYSIS_UTILIZATION_CATEGORY) z.utilization++;
      else if (p.category === ENALYSIS_ROD_LOADS_CATEGORY) z.rodLoads++;
      else if (p.category === ENALYSIS_BLOWBY_CATEGORY) z.blowby++;
    }
    return z;
  }, [allParameters]);

  // Helper: Compare numeric stats (average/zScore) with null handling
  const compareStats = (aValue, bValue) => {
    if (aValue === null && bValue === null) return 0;
    if (aValue === null) return 1; // Nulls go to end
    if (bValue === null) return -1; // Nulls go to end
    return aValue - bValue;
  };

  // Helper: Get stat value for a parameter
  const getStatValue = (param, statType) => {
    const paramId = getParameterId(param);
    const paramStats = stats.get(paramId);
    return paramStats?.[statType] ?? null;
  };

  // Helper: Parse numeric value from string
  const parseNumericValue = (value) => {
    const num = parseFloat(removeCommasAndSpaces(value));
    return isNaN(num) || !isFinite(num) ? 0 : num;
  };

  const sortParameters = (a, b) => {
    let comparison = 0;

    switch (sortColumn) {
      case "tagName":
        comparison = a.parameter.localeCompare(b.parameter);
        break;
      case "value":
        comparison =
          parseNumericValue(a.currentValue) - parseNumericValue(b.currentValue);
        break;
      case "average":
        comparison = compareStats(
          getStatValue(a, "average"),
          getStatValue(b, "average")
        );
        break;
      case "zScore":
        comparison = compareStats(
          getStatValue(a, "zScore"),
          getStatValue(b, "zScore")
        );
        break;
      case "status":
        const statusPriority = {
          Danger: 0,
          Alerted: 1,
          Normal: 2,
          "No Data": 3,
        };
        const aStatus = getParameterStatus(a).label;
        const bStatus = getParameterStatus(b).label;
        comparison =
          (statusPriority[aStatus] ?? 99) - (statusPriority[bStatus] ?? 99);
        break;
      case "unit":
        const aUnit = a.unit || "";
        const bUnit = b.unit || "";
        comparison = aUnit.localeCompare(bUnit);
        break;
    }

    return sortDirection === "asc" ? comparison : -comparison;
  };

  const selectedFilterValues = useMemo(() => {
    if (selectedGroupId !== null) {
      return `group:${selectedGroupId}`;
    }

    if (selectedCategory === ENALYSIS_FILTER_ALL_SENTINEL) {
      return "enalysis:all";
    }

    if (selectedCategory) {
      return `category:${selectedCategory}`;
    }

    if (selectedStatus !== "all") {
      return `status:${selectedStatus}`;
    }

    return "";
  }, [selectedGroupId, selectedCategory, selectedStatus]);

  const handleFilterSelectionChange = useCallback(
    (value) => {
      const normalizedValue = typeof value === "string" ? value : "";

      if (!normalizedValue) {
        setSelectedGroupId(null);
        setSelectedCategory(null);
        setSelectedStatus("all");
        return;
      }

      if (normalizedValue.startsWith("group:")) {
        const nextGroupId = Number(normalizedValue.replace("group:", ""));
        setSelectedGroupId(Number.isNaN(nextGroupId) ? null : nextGroupId);
        setSelectedCategory(null);
        setSelectedStatus("all");
        return;
      }

      if (normalizedValue === "enalysis:all") {
        setSelectedGroupId(null);
        setSelectedCategory(ENALYSIS_FILTER_ALL_SENTINEL);
        setSelectedStatus("all");
        return;
      }

      if (normalizedValue.startsWith("category:")) {
        setSelectedGroupId(null);
        setSelectedCategory(normalizedValue.replace("category:", "") || null);
        setSelectedStatus("all");
        return;
      }

      if (normalizedValue.startsWith("status:")) {
        setSelectedGroupId(null);
        setSelectedCategory(null);
        setSelectedStatus(normalizedValue.replace("status:", "") || "all");
      }
    },
    [setSelectedGroupId, setSelectedCategory, setSelectedStatus]
  );

  const hasActiveFilters =
    selectedGroupId !== null || selectedCategory || selectedStatus !== "all";

  const handleClearFilters = useCallback(() => {
    setSearchTerm("");
    setSelectedGroupId(null);
    setSelectedCategory(null);
    setSelectedStatus("all");
  }, [
    setSearchTerm,
    setSelectedGroupId,
    setSelectedCategory,
    setSelectedStatus,
  ]);

  // Filter and sort parameters
  const filteredAndSortedParameters = useMemo(() => {
    let base = allParameters || [];

    if (selectedGroupId !== null) {
      const selectedGroups = groups.filter(
        (group) => Number(group.TagGroupId) === Number(selectedGroupId)
      );

      base = filterParametersByGroups(base, selectedGroups);
    } else if (selectedCategory === ENALYSIS_FILTER_ALL_SENTINEL) {
      base = base.filter((param) =>
        ENALYSIS_TREND_ROW_CATEGORIES.has(param.category)
      );
    } else if (selectedCategory) {
      base = base.filter((param) => param.category === selectedCategory);
    }

    const seenIds = new Set();
    const uniqueParams = base.filter((param) => {
      const paramId = getParameterId(param);
      if (seenIds.has(paramId)) {
        return false;
      }
      seenIds.add(paramId);
      return true;
    });

    // Apply search and status filters
    let filtered = uniqueParams;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((param) =>
        param.parameter.toLowerCase().includes(searchLower)
      );
    }

    if (selectedStatus !== "all") {
      filtered = filtered.filter((param) =>
        matchesStatusFilter(param, selectedStatus)
      );
    }

    // Sort
    const sorted = [...filtered].sort(sortParameters);

    return sorted;
  }, [
    allParameters,
    searchTerm,
    selectedStatus,
    selectedCategory,
    sortColumn,
    sortDirection,
    stats,
    selectedGroupId,
    groups,
    getParameterId,
    trendColumnRawData,
  ]);

  // Count how many tags on the current asset belong to each group
  const groupAssetTagCounts = useMemo(() => {
    if (!Array.isArray(groups) || groups.length === 0) {
      return {};
    }

    const counts = {};

    groups.forEach((group) => {
      const groupId = Number(group.TagGroupId);
      if (Number.isNaN(groupId)) {
        return;
      }

      const matchingParams = filterParametersByGroups(allParameters || [], [
        group,
      ]);

      const uniqueParamIds = new Set(
        matchingParams.map((param) => getParameterId(param)).filter(Boolean)
      );

      counts[groupId] = uniqueParamIds.size;
    });

    return counts;
  }, [groups, allParameters, getParameterId]);

  // Count parameters by status
  const statusCounts = useMemo(() => {
    return countParametersByStatus(allParameters || []);
  }, [allParameters]);

  const categoryTagCounts = useMemo(() => {
    const counts = {};
    categories.forEach((category) => {
      counts[category] = (allParameters || []).filter(
        (param) => param.category === category
      ).length;
    });
    return counts;
  }, [categories, allParameters]);

  // Stat cell component
  const StatCell = useCallback(
    ({ value, isLoading, unit }) => (
      <TableCell className={cardStyles.tableCell}>
        {isLoading ? (
          <LoadingIndicator size='sm' showText />
        ) : value !== null ? (
          <>
            {formatNumber(value, 2)} {unit && <span>{unit}</span>}
          </>
        ) : (
          <span className='text-muted-foreground'>-</span>
        )}
      </TableCell>
    ),
    []
  );

  // Fetch 7-day data and calculate stats for all parameters (only if not provided by parent)
  useEffect(() => {
    // If trend columns are provided by parent, don't fetch locally
    if (trendColumns !== undefined) {
      return;
    }

    if (!allParameters || allParameters.length === 0) {
      setLocalTrendColumns(new Map());
      return;
    }

    const fetchStatsForParameters = async () => {
      const statsMap = new Map();
      const fetchPromises = allParameters.map(async (param) => {
        if (!param.apiData?.assetTagId || !param.apiData?.assetId) {
          return;
        }

        const paramId = getParameterId(param);
        if (!paramId) return;

        try {
          const apiResponse = await getTagMeasurementsLast7Days(
            param.apiData.assetTagId,
            param.apiData.assetId
          );

          if (
            apiResponse
            && apiResponse.Measurements
            && Array.isArray(apiResponse.Measurements)
          ) {
            // Parse current value for Z-score calculation
            const currentValue = parseFloat(
              removeCommasAndSpaces(param.currentValue)
            );
            const numericCurrentValue =
              !isNaN(currentValue) && isFinite(currentValue)
                ? currentValue
                : null;

            const { average, zScore } = calculateAverageAndZScore(
              apiResponse.Measurements,
              numericCurrentValue
            );
            statsMap.set(paramId, { average, zScore });
          } else {
            statsMap.set(paramId, { average: null, zScore: null });
          }
        } catch (err) {
          console.error(
            `Error fetching 7-day data for ${param.parameter}:`,
            err
          );
          statsMap.set(paramId, { average: null, zScore: null });
        }
      });

      await Promise.all(fetchPromises);
      setLocalTrendColumns(statsMap);
    };

    fetchStatsForParameters();
  }, [allParameters, getParameterId, trendColumns]);

  const graphSelection = useMemo(() => {
    const visibleIds = filteredAndSortedParameters
      .map((param) => getParameterId(param))
      .filter(Boolean);
    const visibleSelectedCount = visibleIds.filter((id) =>
      selectedParameters.has(id)
    ).length;
    const visibleUnselectedCount = visibleIds.length - visibleSelectedCount;
    const allVisibleSelected =
      visibleIds.length > 0 && visibleSelectedCount === visibleIds.length;
    const limitReached = selectedParameters.size >= MAX_SELECTED_GRAPH_TAGS;
    const canBulkSelectVisible =
      visibleUnselectedCount > 0 && !limitReached;

    return {
      visibleIds,
      allVisibleSelected,
      someVisibleSelected:
        visibleSelectedCount > 0 && !allVisibleSelected,
      limitReached,
      bulkSelectDisabled:
        visibleIds.length > 0 && !allVisibleSelected && !canBulkSelectVisible,
      statusLabel: `${selectedParameters.size} of ${MAX_SELECTED_GRAPH_TAGS} tags selected for graphing${limitReached ? " (limit reached)" : ""}`,
      limitTitle:
        limitReached && !allVisibleSelected ? GRAPH_TAG_LIMIT_TITLE : undefined,
    };
  }, [filteredAndSortedParameters, selectedParameters, getParameterId]);

  const setSelectAllHeaderRef = useCallback(
    (node) => {
      if (node) {
        node.indeterminate = graphSelection.someVisibleSelected;
      }
    },
    [graphSelection.someVisibleSelected]
  );

  const handleSelectAll = useCallback(
    (checked) => {
      if (checked) {
        if (selectedParameters.size >= MAX_SELECTED_GRAPH_TAGS) return;
        const idsToAdd = graphSelection.visibleIds.filter(
          (id) => !selectedParameters.has(id)
        );
        setSelectedParameters(
          addGraphTagSelections(selectedParameters, idsToAdd)
        );
      } else {
        setSelectedParameters(
          removeVisibleGraphTagSelections(
            selectedParameters,
            graphSelection.visibleIds
          )
        );
      }
    },
    [graphSelection.visibleIds, selectedParameters, setSelectedParameters]
  );

  const toggleParameterSelection = useCallback(
    (paramId, checked) => {
      if (!paramId) return;

      const next = new Set(selectedParameters);
      if (checked) {
        if (next.size >= MAX_SELECTED_GRAPH_TAGS) return;
        next.add(paramId);
      } else {
        next.delete(paramId);
      }
      setSelectedParameters(next);
    },
    [selectedParameters, setSelectedParameters]
  );

  return (
    <Card
      className={cn(
        "flex h-full w-full max-w-full min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-card shadow-sm",
        selectedParameters.size > 0 ? "transition-all" : ""
      )}>
      <CardContent className='flex min-w-0 flex-shrink-0 flex-col space-y-4 p-6'>
        <div
          className={cn(
            "flex min-w-0 gap-2",
            compactFilterToolbar
              ? "flex-col"
              : "flex-col md:flex-row md:items-end"
          )}>
          <div
            className={cn(
              "min-w-0 w-full",
              !compactFilterToolbar && "md:w-[22%] md:max-w-[14rem]"
            )}>
            <GroupsDropdown
              groups={groups}
              categories={categories}
              groupAssetTagCounts={groupAssetTagCounts}
              categoryTagCounts={categoryTagCounts}
              alarmLevelCounts={statusCounts}
              enalysisMetricsCategory={ENALYSIS_METRICS_CATEGORY}
              enalysisSectionTotalCount={enalysisCounts.total}
              enalysisUtilizationCategory={ENALYSIS_UTILIZATION_CATEGORY}
              enalysisUtilizationCount={enalysisCounts.utilization}
              enalysisRodLoadsCategory={ENALYSIS_ROD_LOADS_CATEGORY}
              enalysisRodLoadsCount={enalysisCounts.rodLoads}
              enalysisBlowbyCategory={ENALYSIS_BLOWBY_CATEGORY}
              enalysisBlowbyCount={enalysisCounts.blowby}
              isLoadingGroups={isLoadingGroups}
              selectedFilterValue={selectedFilterValues}
              setSelectedFilterValue={handleFilterSelectionChange}
            />
          </div>
          <div
            className={cn(
              "relative min-w-0 w-full",
              !compactFilterToolbar && "md:min-w-0 md:flex-1"
            )}>
            <Input
              type='text'
              placeholder='Search tags...'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className='w-full min-w-0 pr-10'
            />
            {searchTerm ? (
              <button
                onClick={() => setSearchTerm("")}
                className='absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground cursor-pointer hover:text-foreground transition-colors z-10'
                title='Clear search'>
                <X className='h-4 w-4' />
              </button>
            ) : (
              <Search className={searchStyles.icon} />
            )}
          </div>
          <div
            className={cn(
              "flex min-w-0 justify-end",
              compactFilterToolbar ? "w-full" : "w-full md:w-auto md:shrink-0"
            )}>
            <Button
              variant='ghost'
              size='sm'
              onClick={handleClearFilters}
              className={cn(
                "gap-1.5 text-muted-foreground hover:bg-muted hover:text-foreground",
                compactFilterToolbar
                  ? "h-auto min-h-8 max-w-full !whitespace-normal px-2 py-1.5"
                  : "h-8 px-2 py-1"
              )}
              data-testid='button-clear-filters'
              title='Clear all filters'>
              Clear Filters
              <X className='h-4 w-4 shrink-0' />
            </Button>
          </div>
        </div>

        {/* Select All */}
        <div className='flex flex-col gap-2 pt-2 border-t border-border'>
          <Button
            variant='outline'
            onClick={() => handleSelectAll(!graphSelection.allVisibleSelected)}
            disabled={graphSelection.bulkSelectDisabled}
            title={graphSelection.limitTitle}
            className={cn(
              "h-auto min-h-10 w-full min-w-0 flex-wrap gap-x-2 gap-y-1 !whitespace-normal px-3 py-2 text-center leading-snug",
              "justify-center rounded-full border-border bg-muted text-foreground hover:bg-muted/80",
              graphSelection.allVisibleSelected
                && cn(steelChromeButton, "rounded-full")
            )}>
            <CheckSquare className='h-4 w-4 shrink-0' aria-hidden />
            <span className='min-w-0 break-words'>
              Select All ({filteredAndSortedParameters.length})
            </span>
          </Button>
          <p className='text-center text-xs text-muted-foreground'>
            {graphSelection.statusLabel}
          </p>
          {selectedParameters.size > 0 && (
            <Button
              variant='outline'
              size='sm'
              onClick={() => setSelectedParameters(new Set())}
              className='h-auto min-h-8 w-full min-w-0 flex-wrap justify-center gap-x-1.5 gap-y-0.5 self-center rounded-full border-border bg-muted px-3 py-1.5 text-center text-foreground !whitespace-normal leading-snug hover:bg-muted/80'>
              <X className='h-4 w-4 shrink-0' aria-hidden />
              <span className='min-w-0 break-words'>
                Clear ({selectedParameters.size})
              </span>
            </Button>
          )}
        </div>
      </CardContent>

      {/* Parameters Table */}
      <div
        className={cn(
          cardStyles.tableContainer,
          "flex-1 min-h-0 overflow-y-auto"
        )}>
        <Table className='w-full'>
          <TableHeader className={cardStyles.tableHeader}>
            <TableRow className={cardStyles.tableRow}>
              <TableHead className={cn(cardStyles.tableHead, "w-12")}>
                <Checkbox
                  ref={setSelectAllHeaderRef}
                  checked={
                    graphSelection.visibleIds.length > 0
                    && graphSelection.allVisibleSelected
                  }
                  disabled={
                    graphSelection.visibleIds.length === 0
                    || graphSelection.bulkSelectDisabled
                  }
                  title={graphSelection.limitTitle}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleSelectAll(e.target.checked);
                  }}
                />
              </TableHead>
              <TableHead className={cardStyles.tableHead}>
                <SortableTableHeader
                  column='tagName'
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onClick={() => handleSort("tagName")}>
                  Tag Name
                </SortableTableHeader>
              </TableHead>
              <TableHead className={cardStyles.tableHead}>
                <SortableTableHeader
                  column='value'
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onClick={() => handleSort("value")}>
                  Current Value
                </SortableTableHeader>
              </TableHead>
              <TableHead className={cardStyles.tableHead}>
                <SortableTableHeader
                  column='unit'
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onClick={() => handleSort("unit")}>
                  Unit
                </SortableTableHeader>
              </TableHead>
              <TableHead className={cardStyles.tableHead}>Gauge</TableHead>
              <TableHead className={cardStyles.tableHead}>
                <div className='flex flex-col'>
                  <span>Trend</span>
                  <span className='text-xs font-normal text-muted-foreground'>
                    7 day
                  </span>
                </div>
              </TableHead>
              <TableHead className={cardStyles.tableHead}>
                <SortableTableHeader
                  column='average'
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onClick={() => handleSort("average")}>
                  <div className='flex flex-col'>
                    <span>Average</span>
                    <span className='text-xs font-normal text-muted-foreground'>
                      7 day
                    </span>
                  </div>
                </SortableTableHeader>
              </TableHead>
              <TableHead className={cardStyles.tableHead}>
                <SortableTableHeader
                  column='zScore'
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onClick={() => handleSort("zScore")}>
                  <div className='flex flex-col'>
                    <div className='flex items-center gap-1'>
                      Z-Score
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type='button'
                            className='inline-flex items-center justify-center cursor-help'
                            onClick={(e) => e.stopPropagation()}
                            aria-label='Z-Score information'>
                            <Info className={cn("h-3.5 w-3.5", infoTooltipIcon)} />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent
                          side='top'
                          sideOffset={8}
                          className='max-w-xs p-3 text-xs'>
                          <p>
                            This Z-Score tells you how far the current value is
                            from the recent average. If the score is greater
                            than 3 (or less than -3), the tag is behaving
                            unusually — either a spike or a drop. It's a quick
                            way to spot issues without digging into every tag
                            manually.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <span className='text-xs font-normal text-muted-foreground'>
                      7 day
                    </span>
                  </div>
                </SortableTableHeader>
              </TableHead>
              <TableHead className={cardStyles.tableHead}>
                <SortableTableHeader
                  column='status'
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onClick={() => handleSort("status")}>
                  Status
                </SortableTableHeader>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredAndSortedParameters.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  className='text-center py-8 text-muted-foreground'>
                  {selectedGroupId !== null || selectedCategory
                    ? "No parameters found matching selected filter"
                    : "No parameters found"}
                </TableCell>
              </TableRow>
            ) : (
              filteredAndSortedParameters.map((param, index) => {
                const paramId = getParameterId(param);
                const isSelected = selectedParameters.has(paramId);
                const isSelectionDisabled =
                  !isSelected && graphSelection.limitReached;
                const paramStats = stats.get(paramId);
                const average = paramStats?.average ?? null;
                const zScore = paramStats?.zScore ?? null;
                const hasData =
                  trendColumnRawData?.has(paramId) || paramStats !== undefined;
                const enalysisTrendsLoading =
                  isEnalysisTrendParamRow(param)
                  && isLoadingEnalysisTrendValues;
                const showLoading =
                  enalysisTrendsLoading
                  || (isLoadingTrendColumns && !hasData);

                return (
                  <TableRow
                    key={paramId}
                    className={cn(
                      cardStyles.bodyRow,
                      isSelectionDisabled
                        ? "cursor-not-allowed opacity-60"
                        : "cursor-pointer hover:bg-muted dark:hover:bg-white/10",
                      isSelected && steelRowSelected
                    )}
                    onClick={() => {
                      if (isSelectionDisabled) return;
                      toggleParameterSelection(paramId, !isSelected);
                    }}>
                    <TableCell className={cardStyles.tableCell}>
                      <Checkbox
                        checked={isSelected}
                        disabled={isSelectionDisabled}
                        title={
                          isSelectionDisabled ? GRAPH_TAG_LIMIT_TITLE : undefined
                        }
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleParameterSelection(paramId, e.target.checked);
                        }}
                      />
                    </TableCell>
                    <TableCell className={cardStyles.tableCell}>
                      {param.parameter}
                    </TableCell>
                    <TableCell className={cardStyles.tableCell}>
                      {enalysisTrendsLoading ? (
                        <LoadingIndicator size='sm' showText />
                      ) : (
                        formatValue(param)
                      )}
                    </TableCell>
                    <TableCell className={cardStyles.tableCell}>
                      {param.unit ? (
                        param.unit
                      ) : (
                        <span className='text-muted-foreground'>-</span>
                      )}
                    </TableCell>
                    <TableCell className={cardStyles.tableCell}>
                      {param.displayType === "Gauge" ? (
                        <MiniHorizontalGauge param={param} />
                      ) : (
                        <span className='text-muted-foreground'>-</span>
                      )}
                    </TableCell>
                    <TableCell className={cardStyles.tableCell}>
                      {showLoading ? (
                        <LoadingIndicator size='sm' showText />
                      ) : trendColumnRawData?.get(paramId) ? (
                        <MiniTrendSparkline
                          rawData={trendColumnRawData.get(paramId)}
                          unit={param.unit}
                        />
                      ) : (
                        <span className='text-muted-foreground'>-</span>
                      )}
                    </TableCell>
                    <StatCell value={average} isLoading={showLoading} />
                    <StatCell value={zScore} isLoading={showLoading} />
                    <TableCell className={cardStyles.tableCell}>
                      <ParameterStatusBadge param={param} />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}
